import { 
  doc, 
  getDoc, 
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Save counselor's private notes for a couple
 */
export const saveCounselorNotes = async (coupleId, counselorId, notes) => {
  try {
    const notesRef = doc(db, 'counselorNotes', `${coupleId}_${counselorId}`);
    
    await setDoc(notesRef, {
      coupleId,
      counselorId,
      notes,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error saving counselor notes:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get counselor's notes for a couple
 */
export const getCounselorNotes = async (coupleId, counselorId) => {
  try {
    const notesDoc = await getDoc(doc(db, 'counselorNotes', `${coupleId}_${counselorId}`));
    
    if (notesDoc.exists()) {
      return { notes: notesDoc.data().notes, error: null };
    }
    
    return { notes: '', error: null };
  } catch (error) {
    console.error('Error getting counselor notes:', error);
    return { notes: '', error: error.message };
  }
};

/**
 * Generate a unique 6-character counselor code
 * Format: XXXXXX (all uppercase letters and numbers)
 * Example: ABC123, XY4Z9K, N3P8QR
 */
export const generateCounselorCode = () => {
  // Use only uppercase letters and numbers (avoid confusing characters like 0, O, I, 1)
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return code;
};

/**
 * Check if a counselor code is unique in the database
 */
export const isCounselorCodeUnique = async (code) => {
  const q = query(
    collection(db, 'users'),
    where('counselorCode', '==', code),
    where('role', '==', 'counselor')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.empty; // Returns true if code is unique
};

/**
 * Generate a unique counselor code (checks for duplicates)
 * This is the main function to use when creating a new counselor
 */
export const generateUniqueCounselorCode = async () => {
  let code;
  let isUnique = false;
  let attempts = 0;
  const maxAttempts = 10;
  
  while (!isUnique && attempts < maxAttempts) {
    code = generateCounselorCode();
    isUnique = await isCounselorCodeUnique(code);
    attempts++;
  }
  
  if (!isUnique) {
    // Fallback: add timestamp to ensure uniqueness
    const timestamp = Date.now().toString(36).slice(-2).toUpperCase();
    code = generateCounselorCode().slice(0, 4) + timestamp;
  }
  
  return code;
};

/**
 * Get counselor by counselor code
 */
export const getCounselorByCode = async (code) => {
  const q = query(
    collection(db, 'users'),
    where('counselorCode', '==', code.toUpperCase()),
    where('role', '==', 'counselor')
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  return snapshot.docs[0].data();
};